const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Client-account administration belongs to the staff session. Public client
// sign-in/recovery endpoints must never expire either authenticated session.
function sessionKey(path = ''): 'vop_user' | 'vop_client_user' | null {
  if (path === '/auth/login') return null;
  if (path.startsWith('/portal/')) return 'vop_client_user';
  if (path.startsWith('/client-auth/') && path !== '/client-auth/invite' && !path.startsWith('/client-auth/status/')) return null;
  return 'vop_user';
}

function getToken(path?: string): string | null {
  const key = sessionKey(path);
  const user = key ? localStorage.getItem(key) : null;
  if (!user) return null;
  try {
    return JSON.parse(user).token || null;
  } catch {
    return null;
  }
}

function expireSession(path: string) {
  const key = sessionKey(path);
  if (!key) return;
  localStorage.removeItem(key);
  window.dispatchEvent(new Event(key === 'vop_user' ? 'vop:unauthorized' : 'vop:client-unauthorized'));
}

async function request<T = any>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken(path);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    if (res.status === 401) expireSession(path);
    throw new Error(body.error || `Request failed: ${res.status}`);
  }

  return res.json();
}

async function previewDocument(path: string, signal?: AbortSignal): Promise<Blob> {
  const token=getToken(path);
  const response=await fetch(`${API_BASE}${path}`,{signal,headers:token?{Authorization:`Bearer ${token}`}:{}});
  if(!response.ok){
    if(response.status===401)expireSession(path);
    const body=await response.json().catch(()=>({}));
    throw new Error(body.error||'Could not load the PDF preview. Try again.');
  }
  const blob=await response.blob();
  if(!blob.size||!blob.type.toLowerCase().startsWith('application/pdf'))throw new Error('The server did not return a PDF preview. Try again.');
  return blob;
}

async function openDocument(path: string): Promise<void> {
  const tab = window.open('', '_blank');
  try {
    const token = getToken();
    const res = await fetch(`${API_BASE}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (res.status === 401) {
      localStorage.removeItem('vop_user');
      window.dispatchEvent(new Event('vop:unauthorized'));
    }
    if (!res.ok) throw new Error('Could not open document');
    const url = URL.createObjectURL(await res.blob());
    if (tab) tab.location.href = url;
    else window.location.href = url;
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (error) {
    tab?.close();
    throw error;
  }
}

// ─── Auth ─────────────────────────────────────────────────
export const api = {
  otpCancellations: {
    get: (id:string) => request('/otp-cancellations/'+id),
    create: (id:string,body:any) => request('/otp-cancellations/'+id,{method:'POST',body:JSON.stringify(body)}),
    action: (id:string,action:string,body:any) => request('/otp-cancellations/case/'+id+'/'+action,{method:'POST',body:JSON.stringify(body)}),
  },
  accreditation: {
    export: (path: string) => openDocument('/accreditation' + path),
    get: (path: string) => request('/accreditation' + path),
    post: (path: string, body: any) => request('/accreditation' + path, {method:'POST',body:JSON.stringify(body)}),
    file: (id: string) => openDocument('/accreditation/files/'+encodeURIComponent(id)+'/download'),
  },
  evidence: {
    capabilities: () => request('/evidence/capabilities'),
    list: (kind: 'clients' | 'lots', id: string, page = 1) => request(`/evidence?${kind === 'clients' ? 'clientId' : 'lotId'}=${encodeURIComponent(id)}&page=${page}`),
    versions: (id: string, page = 1) => request(`/evidence/${id}/versions?page=${page}`),
    upload: async (kind: 'clients' | 'lots', ownerId: string, file: File, title: string, requestId: string, document?: { id: string; revision: number }) => {
      const token = getToken();
      const mime = file.type || (/\.pdf$/i.test(file.name) ? 'application/pdf' : /\.png$/i.test(file.name) ? 'image/png' : /\.jpe?g$/i.test(file.name) ? 'image/jpeg' : '');
      const response = await fetch(`${API_BASE}/evidence/${document ? `${document.id}/versions` : `${kind}/${ownerId}`}`, { method: 'POST', headers: {
        'Content-Type': 'application/octet-stream', ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'X-File-Name': encodeURIComponent(file.name), 'X-File-Type': mime, 'X-Document-Title': encodeURIComponent(title), 'X-Upload-Id': requestId,
        ...(document ? { 'X-Expected-Revision': String(document.revision) } : {}),
      }, body: file });
      if (response.status === 401) { localStorage.removeItem('vop_user'); window.dispatchEvent(new Event('vop:unauthorized')); }
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'File upload failed.');
      return result;
    },
    download: async (documentId: string, revision: number, originalName: string) => {
      const token = getToken();
      const response = await fetch(`${API_BASE}/evidence/${documentId}/versions/${revision}/download`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (response.status === 401) { localStorage.removeItem('vop_user'); window.dispatchEvent(new Event('vop:unauthorized')); }
      if (!response.ok) { const result = await response.json(); throw new Error(result.error || 'Could not download supporting file.'); }
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement('a'); link.href = url; link.download = originalName; document.body.appendChild(link); link.click(); link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
  },
  productionPolicies: {
    users: () => request('/production-policies/users'),
    get: () => request('/production-policies'),
    save: (sectionId: string, data: any) => request(`/production-policies/${sectionId}`, { method: 'PUT', body: JSON.stringify(data) }),
    history: (page = 1) => request(`/production-policies/history?page=${page}`),
  },
  productionDocuments: {
    list: (draftId: string) => request(`/production-documents?draftId=${encodeURIComponent(draftId)}`),
    create: (data: any) => request('/production-documents', { method: 'POST', body: JSON.stringify(data) }),
    action: (id: string, action: string, data: any) => request(`/production-documents/${id}/${action}`, { method: 'POST', body: JSON.stringify(data) }),
    pdf: (id: string, revision?: number) => openDocument(`/production-documents/${id}/pdf${revision===undefined?'':`?revision=${revision}`}`),
  },
  productionTemplates: {
    list: (page: number, filters: {search?:string;kind?:string;status?:string} = {}) => request(`/production-templates?${new URLSearchParams({page:String(page),...filters})}`),
    create: (data: any) => request('/production-templates', { method: 'POST', body: JSON.stringify(data) }),
    review: (id: string, data: any) => request(`/production-templates/${id}/review`, { method: 'POST', body: JSON.stringify(data) }),
    preview: (id: string, signal?: AbortSignal) => previewDocument(`/production-templates/${id}/preview`,signal),
  },
  interments: {
    list: (lotId: string) => request(`/interments/lot/${lotId}`),
    calendar: (from: string, to: string) => request(`/interments/calendar?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
    create: (body: any) => request('/interments', {method:'POST',body:JSON.stringify(body)}),
    capacity: (lotId: string, body: any) => request(`/interments/capacity/${lotId}`, {method:'PUT',body:JSON.stringify(body)}),
    action: (id: string, action: string, body: any) => request(`/interments/${id}/${action}`, {method:'POST',body:JSON.stringify(body)}),
    pdf: (id: string, kind: string, revision?: number) => openDocument(`/interments/${id}/pdf/${kind}${revision===undefined?'':`?revision=${revision}`}`),
    history: (id: string, page = 1) => request(`/interments/${id}/history?page=${page}`),
  },
  transfers: {
    list: (lotId: string) => request(`/ownership-transfers/lot/${lotId}`),
    create: (body: any) => request('/ownership-transfers', {method:'POST', body:JSON.stringify(body)}),
    action: (id: string, action: string, body: any) => request(`/ownership-transfers/${id}/${action}`, {method:'POST',body:JSON.stringify(body)}),
    feeClearance: (id: string, feeId: string, body: any) => request(`/ownership-transfers/${id}/fees/${feeId}/clearance`, {method:'POST',body:JSON.stringify(body)}),
    pdf: (id: string, kind: string, preview = false) => openDocument(`/ownership-transfers/${id}/pdf/${kind}${preview?'?preview=1':''}`),
    history: (id: string, page = 1) => request(`/ownership-transfers/${id}/history?page=${page}`),
  },
  ownership: { get: (lotId: string, page: number) => request(`/production-ownership/${lotId}?page=${page}`) },
  propertyCases: {fee:(id:string,action:string,data:any)=>request(`/property-cases/${id}/fee/${action}`,{method:'POST',body:JSON.stringify(data)}),get:(id:string)=>request(`/property-cases/lot/${id}`),create:(id:string,data:any)=>request(`/property-cases/lot/${id}`,{method:'POST',body:JSON.stringify(data)}),review:(id:string,data:any)=>request(`/property-cases/${id}/review`,{method:'POST',body:JSON.stringify(data)})},
  penalties: {get:(id:string)=>request(`/penalty-assessments/${id}`),prepare:(id:string,data:any)=>request(`/penalty-assessments/${id}`,{method:'POST',body:JSON.stringify(data)}),review:(id:string,data:any)=>request(`/penalty-assessments/case/${id}/review`,{method:'POST',body:JSON.stringify(data)})},
  otpReceipts: {
    instrument: (draftId:string,receiptId:string,data:any) => request(`/otp-receipts/${draftId}/${receiptId}/instrument`, {method:'POST',body:JSON.stringify(data)}),
    statement: (id: string) => openDocument(`/otp-receipts/${id}/statement.pdf`),
    get: (id: string) => request(`/otp-receipts/${id}`),
    record: (id: string, data: any) => request(`/otp-receipts/${id}`, { method: 'POST', body: JSON.stringify(data) }),
    clearance: (id: string, receiptId: string, data: any) => request(`/otp-receipts/${id}/${receiptId}/clearance`, { method: 'POST', body: JSON.stringify(data) }),
  },
  otpQuotations: {
    workflow: (id: string) => request(`/otp-quotations/${id}/workflow`),
    calculate: (id: string, data: any) => request(`/otp-quotations/${id}/calculate`, { method: 'POST', body: JSON.stringify(data) }),
    list: (page: number) => request(`/otp-quotations?page=${page}`),
    get: (id: string) => request(`/otp-quotations/${id}`),
    save: (id: string, data: any) => request(`/otp-quotations/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    review: (id: string, data: any) => request(`/otp-quotations/${id}/review`, { method: 'POST', body: JSON.stringify(data) }),
  },
  otpPreparations: {
    signature: (id: string, data: any) => request(`/otp-preparations/${id}/signature`, { method: 'POST', body: JSON.stringify(data) }),
    issue: (id: string, data: any) => request(`/otp-preparations/${id}/issue`, { method: 'POST', body: JSON.stringify(data) }),
    issuedPdf: (id: string, documentId: string) => openDocument(`/otp-preparations/${id}/issued/${documentId}/pdf`),
    formattedIssuedPdf: (id: string, documentId: string) => openDocument(`/otp-preparations/${id}/issued/${documentId}/pdf?formatted=1`),
    list: (page = 1, search = '') => request(`/otp-preparations?${new URLSearchParams({ page: String(page), search })}`),
    get: (id: string) => request(`/otp-preparations/${id}`),
    preview: (data: any) => request('/otp-preparations/preview', { method: 'POST', body: JSON.stringify(data) }),
    create: (data: any) => request('/otp-preparations', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => request(`/otp-preparations/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    pdf: (id: string, revision?: number) => openDocument(`/otp-preparations/${id}/pdf${revision === undefined ? '' : `?revision=${revision}`}`),
  },
  auth: {
    login: (email: string, password: string) =>
      request('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
    me: () => request('/auth/me'),
    register: (data: { email: string; password: string; name: string; role?: string; phone?: string }) =>
      request('/auth/register', { method: 'POST', body: JSON.stringify(data) }),
  },

  agents: {
    list: () => request('/agents'),
    update: (id: string, data: { name: string; phone: string; expectedUpdatedAt: string }) => request(`/agents/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  },

  // ─── Clients ──────────────────────────────────────────────
  clients: {
    corrections: (id: string, page = 1) => request(`/clients/${id}/corrections?page=${page}`),
    list: (params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return request(`/clients${qs}`);
    },
    get: (id: string) => request(`/clients/${id}`),
    create: (data: any) => request('/clients', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => request(`/clients/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: string) => request(`/clients/${id}`, { method: 'DELETE' }),
    stats: () => request('/clients/stats/summary'),
    addBeneficiary: (clientId: string, data: any) =>
      request(`/clients/${clientId}/beneficiaries`, { method: 'POST', body: JSON.stringify(data) }),
    deleteBeneficiary: (beneficiaryId: string, data: { correctionReason: string; evidenceReference: string; evidenceVersionId?: string | null; expectedUpdatedAt: string }) =>
      request(`/clients/beneficiaries/${beneficiaryId}`, { method: 'DELETE', body: JSON.stringify(data) }),
    addContactLog: (clientId: string, data: any) =>
      request(`/clients/${clientId}/contact-logs`, { method: 'POST', body: JSON.stringify(data) }),
  },

  // ─── Sales ────────────────────────────────────────────────
  sales: {
    board: () => request('/sales/board'),
    list: (params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return request(`/sales${qs}`);
    },
    get: (id: string) => request(`/sales/${id}`),
    create: (data: any) => request('/sales', { method: 'POST', body: JSON.stringify(data) }),
    updateStage: (id: string, stage: string, expectedUpdatedAt: string, beforeId: string | null = null) =>
      request(`/sales/${id}/stage`, { method: 'PUT', body: JSON.stringify({ stage, expectedUpdatedAt, beforeId }) }),
    update: (id: string, data: any) => request(`/sales/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    recordPayment: (paymentId: string, data: any) =>
      request(`/sales/payments/${paymentId}/pay`, { method: 'POST', body: JSON.stringify(data) }),
    stats: () => request('/sales/stats/summary'),
  },

  // ─── Lots (legacy) ──────────────────────────────────────────
  lots: {
    list: () => request('/lots'),
    create: (data: any) => request('/lots', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => request(`/lots/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  },

  // ─── Inventory (full CRUD) ─────────────────────────────────
  inventory: {
    saveLayout: (data: { sections: any[]; lots: any[] }) => request('/inventory/layout', { method: 'PUT', body: JSON.stringify(data) }),
    // Sections
    sections: {
      list: () => request('/inventory/sections'),
      get: (id: string) => request(`/inventory/sections/${id}`),
      create: (data: any) => request('/inventory/sections', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: any) => request(`/inventory/sections/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      delete: (id: string) => request(`/inventory/sections/${id}`, { method: 'DELETE' }),
    },
    // Lot Types
    lotTypes: {
      list: () => request('/inventory/lot-types'),
      create: (data: any) => request('/inventory/lot-types', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: any) => request(`/inventory/lot-types/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      delete: (id: string) => request(`/inventory/lot-types/${id}`, { method: 'DELETE' }),
    },
    // Lots
    lots: {
      list: (params?: Record<string, string>) => {
        const qs = params ? '?' + new URLSearchParams(params).toString() : '';
        return request(`/inventory/lots${qs}`);
      },
      get: (id: string) => request(`/inventory/lots/${id}`),
      create: (data: any) => request('/inventory/lots', { method: 'POST', body: JSON.stringify(data) }),
      batchCreate: (data: any) => request('/inventory/lots/batch', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: any) => request(`/inventory/lots/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      delete: (id: string) => request(`/inventory/lots/${id}`, { method: 'DELETE' }),
    },
    // Transactions (Map → Sale)
    transactions: {
      context: (lotId: string) => request(`/inventory/lots/${lotId}/transaction`),
      reserve: (lotId: string, data: { clientId: string; agentId: string; saleAmount: number; paymentType?: string; downPayment?: number; installmentMonths?: number; notes?: string; performedBy?: string }) =>
        request(`/inventory/lots/${lotId}/reserve`, { method: 'POST', body: JSON.stringify(data) }),
      release: (lotId: string, data?: { reason?: string; performedBy?: string }) =>
        request(`/inventory/lots/${lotId}/release`, { method: 'POST', body: JSON.stringify(data || {}) }),
      completeSale: (lotId: string, data?: { performedBy?: string }) =>
        request(`/inventory/lots/${lotId}/complete-sale`, { method: 'POST', body: JSON.stringify(data || {}) }),
      searchClients: (q: string) => request(`/inventory/clients/search?q=${encodeURIComponent(q)}`),
      agents: () => request('/inventory/agents'),
    },
    // Audit Logs
    auditLogs: {
      list: (lotId: string) => request(`/inventory/lots/${lotId}/audit-logs`),
      create: (lotId: string, data: any) => request(`/inventory/lots/${lotId}/audit-logs`, { method: 'POST', body: JSON.stringify(data) }),
    },
    // Policies
    policies: {
      list: () => request('/inventory/policies'),
      update: (id: string, data: any) => request(`/inventory/policies/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    },
  },

  // ─── Dashboard ─────────────────────────────────────────────
  dashboard: {
    stats: () => request('/dashboard/stats'),
  },

  // ─── Reports ──────────────────────────────────────────────
  reports: {
    summary: (params?: { year: string; period: string }) => request('/reports/summary' + (params ? '?' + new URLSearchParams(params).toString() : '')),
    aging: () => request('/reports/aging'),
    revenueTrend: () => request('/reports/revenue-trend'),
  },

  // ─── Commission Rules ─────────────────────────────────────
  commissionRules: {
    list: () => request('/commission-rules'),
    get: (id: string) => request(`/commission-rules/${id}`),
    create: (data: any) => request('/commission-rules', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => request(`/commission-rules/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: string, expectedUpdatedAt: string) => request(`/commission-rules/${id}`, { method: 'DELETE', body: JSON.stringify({expectedUpdatedAt}) }),
    toggle: (id: string, is_active: boolean, expectedUpdatedAt: string) => request(`/commission-rules/${id}/toggle`, { method: 'PATCH', body: JSON.stringify({is_active,expectedUpdatedAt}) }),
    options: () => request('/commission-rules/options'),
    test: (data: any) =>
      request('/commission-rules/test', { method: 'POST', body: JSON.stringify(data) }),
    agentSummary: () => request('/commission-rules/reports/agent-summary'),
  },

  // ─── Notifications ──────────────────────────────────────────
  notifications: {
    list: (userId: string, params?: Record<string, string>) => {
      const qs = params ? '&' + new URLSearchParams(params).toString() : '';
      return request(`/notifications/${userId}?limit=30${qs}`);
    },
    markRead: (id: string) => request(`/notifications/${id}/read`, { method: 'PATCH' }),
    markAllRead: (userId: string) => request(`/notifications/user/${userId}/read-all`, { method: 'PATCH' }),
    delete: (id: string) => request(`/notifications/${id}`, { method: 'DELETE' }),
    generate: (userId: string) => request(`/notifications/generate/${userId}`, { method: 'POST' }),
  },

  // ─── Documents (PDF) ──────────────────────────────────────
  documents: {
    list: (saleId: string) => request(`/documents/list/${saleId}`),
    openSaleContract: (saleId: string) => openDocument(`/documents/sale-contract/${saleId}`),
    openSoa: (saleId: string) => openDocument(`/documents/soa/${saleId}`),
    openPaymentReceipt: (paymentId: string) => openDocument(`/documents/payment-receipt/${paymentId}`),
  },

  // ─── Payments + Ledger ──────────────────────────────────────
  payments: {
    accounts: () => request('/payments/accounts'),
    // Schedules
    schedules: {
      list: (params?: Record<string, string>) => {
        const qs = params ? '?' + new URLSearchParams(params).toString() : '';
        return request(`/payments/schedules${qs}`);
      },
      get: (id: string) => request(`/payments/schedules/${id}`),
      generate: (data: { expectedUpdatedAt: string; saleId: string; frequency?: string; startDate?: string; customDates?: string[] }) =>
        request('/payments/schedules/generate', { method: 'POST', body: JSON.stringify(data) }),
    },
    // Record payment (FIFO)
    record: (data: { requestId: string; expectedUpdatedAt: string; saleId: string; amount: number; paymentMethod?: string; referenceNumber?: string; receiptNumber?: string; notes?: string; performedBy?: string }) =>
      request('/payments/record', { method: 'POST', body: JSON.stringify(data) }),
    // Overdue
    overdue: {
      detect: () => request('/payments/overdue/detect', { method: 'POST' }),
      summary: () => request('/payments/overdue/summary'),
    },
    // Penalties
      penalties: {
      compute: (data?: { penaltyRate?: number; minDaysOverdue?: number; preview?: boolean; fingerprint?: string }) =>
        request('/payments/penalties/compute', { method: 'POST', body: JSON.stringify(data || {}) }),
    },
    // Ledger
    ledger: {
      list: (params?: Record<string, string>) => {
        const qs = params ? '?' + new URLSearchParams(params).toString() : '';
        return request(`/payments/ledger${qs}`);
      },
      create: (data: { requestId: string; clientId: string; saleId?: string; type: string; amount: number; description: string; referenceNumber?: string; performedBy?: string }) =>
        request('/payments/ledger', { method: 'POST', body: JSON.stringify(data) }),
    },
    // Balance
    balance: (clientId: string) => request(`/payments/balance/${clientId}`),
    // Stats
    stats: () => request('/payments/stats'),
  },

  // ─── Users & Roles ──────────────────────────────────────
  users: {
    list: (params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return request(`/users${qs}`);
    },
    get: (id: string) => request(`/users/${id}`),
    create: (data: any) => request('/users', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: string, data: any) => request(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    toggleStatus: (id: string) => request(`/users/${id}/toggle-status`, { method: 'PATCH' }),
    delete: (id: string) => request(`/users/${id}`, { method: 'DELETE' }),
    myPermissions: () => request('/users/me/permissions'),
    // Roles
    roles: {
      list: () => request('/users/roles/list'),
      get: (id: string) => request(`/users/roles/${id}`),
      create: (data: any) => request('/users/roles', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: any) => request(`/users/roles/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      delete: (id: string) => request(`/users/roles/${id}`, { method: 'DELETE' }),
      updatePermissions: (id: string, permissions: { module: string; action: string }[]) =>
        request(`/users/roles/${id}/permissions`, { method: 'PUT', body: JSON.stringify({ permissions }) }),
    },
  },

  // ─── Client Auth ─────────────────────────────────────────
  clientAuth: {
    invite: (clientId: string) =>
      request('/client-auth/invite', { method: 'POST', body: JSON.stringify({ clientId }) }),
    verifyInvite: (token: string) => request(`/client-auth/verify-invite/${token}`),
    setupPassword: (token: string, password: string) =>
      request('/client-auth/setup-password', { method: 'POST', body: JSON.stringify({ token, password }) }),
    login: (email: string, password: string) =>
      request('/client-auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
    forgotPassword: (email: string) =>
      request('/client-auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),
    resetPassword: (token: string, password: string) =>
      request('/client-auth/reset-password', { method: 'POST', body: JSON.stringify({ token, password }) }),
    status: (clientId: string) => request(`/client-auth/status/${clientId}`),
  },

  // ─── Client Portal ─────────────────────────────────────────
  portal: {
    dashboard: () => request('/portal/dashboard'),
    me: () => request('/portal/me'),
    updateProfile: (data: any) => request('/portal/me', { method: 'PUT', body: JSON.stringify(data) }),
    changePassword: (currentPassword: string, newPassword: string) =>
      request('/portal/me/password', { method: 'PUT', body: JSON.stringify({ currentPassword, newPassword }) }),
    purchases: () => request('/portal/purchases'),
    purchase: (id: string) => request(`/portal/purchases/${id}`),
    payments: (params?: Record<string, string>) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : '';
      return request(`/portal/payments${qs}`);
    },
    documents: () => request('/portal/documents'),
  },

  // ─── Global Search ───────────────────────────────────────
  search: (q: string) => request(`/search?q=${encodeURIComponent(q)}`),

  // ─── Refunds ──────────────────────────────────────────────
  refunds: {
    // Policies
    policies: {
      list: () => request('/refunds/policies'),
      get: (id: string) => request(`/refunds/policies/${id}`),
      create: (data: any) => request('/refunds/policies', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: any) => request(`/refunds/policies/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
      delete: (id: string) => request(`/refunds/policies/${id}`, { method: 'DELETE' }),
      toggle: (id: string) => request(`/refunds/policies/${id}/toggle`, { method: 'PATCH' }),
    },
    // Calculate refund
    calculate: (data: { saleId: string; policyId?: string }) =>
      request('/refunds/calculate', { method: 'POST', body: JSON.stringify(data) }),
    // Process refund
    process: (data: { requestId: string; saleId: string; policyId?: string; refundAmount: number; refundMethod: string; referenceNumber?: string; notes?: string; performedBy?: string }) =>
      request('/refunds/process', { method: 'POST', body: JSON.stringify(data) }),
  },
};
